import React, { FC, MutableRefObject, ReactElement, useCallback, useRef, useState } from 'react'
import Tooltip from '@material-ui/core/Tooltip'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { ReactState } from '../../../../React/Common/Types/Types'
import { UsersListLanguage } from '../../../../../i18n'
import { UserMoreInformation } from '../../../../NonReact/Types/UsersListTypes'
import { getUserMoreInformation } from '../../../../NonReact/common/API/EntityAPIClient'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'
import { IsVarEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

type Props = {
    /** Application user the totals belong to. */
    readonly username: string
    /** Language handler with the users list bundle loaded. */
    readonly language: CLanguageHandler
}

/** One line of the tooltip: a caption and the value beside it. */
type MoreInformationLine = {
    readonly label: string
    readonly value: string
}

/**
 * How long the fetched totals are treated as current for.
 *
 * Open sessions and the reports they are running are live values that the classic page recomputes on every
 * render, so keeping the first response for the life of the row would show a stale count indefinitely. A short
 * window still spares a list of many users a request per hover, and also absorbs the focus and hover handlers
 * both asking on the same interaction.
 */
const LIVE_TOTALS_TTL_MILLISECONDS: number = 10000

/** Marker for "never fetched", so the first hover always asks. */
const NOT_LOADED: number = 0

/**
 * Reads a count, treating an absent value as zero.
 *
 * @param value - Count from the endpoint, absent when it did not serialise the field.
 * @returns The count as display text.
 */
function formatCount(value: Definable<number>): string {
    return String(value ?? 0)
}

/**
 * Reads one of the endpoint's pre-formatted timestamps.
 *
 * The endpoint formats both timestamps in the viewer's own Akixi time zone and locale, so that the tooltip and
 * the list's Last Signed In column cannot describe one event at two different times. The local fallback covers a
 * payload that omitted the field entirely.
 *
 * @param language - Language handler, used for the "never" wording.
 * @param value - Formatted timestamp from the endpoint.
 * @returns The timestamp text, or the "never" placeholder.
 */
function formatTimestamp(language: CLanguageHandler, value: Definable<string>): string {
    return isStringNotEmpty(value) ? value : language.get(UsersListLanguage.UsersList_MoreInfo_Never)
}

/**
 * Decides whether a response actually carries totals.
 *
 * A 200 with an empty body resolves as `{}`, which is not caught by an emptiness check on the object itself, so
 * without this the tooltip would render a complete set of zeroes and a "[Never]" as though they were real.
 *
 * @param response - Response from the endpoint.
 * @returns True when the response holds at least one total.
 */
function hasTotals(response: JSONResponse<UserMoreInformation>): boolean {
    return !IsVarEmpty(response) && Object.keys(response).length > 0
}

/**
 * Builds the tooltip's lines in the order the classic user list shows them.
 *
 * @param language - Language handler with the users list bundle loaded.
 * @param info - Totals returned for the user.
 * @returns Caption and value pairs to render.
 */
function buildLines(language: CLanguageHandler, info: UserMoreInformation): MoreInformationLine[] {
    return [
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_LastSignedInAt),
            value: formatTimestamp(language, info.lastSignedInAt)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_TotalTimesSignedIn),
            value: formatCount(info.totalTimesSignedIn)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_ActiveSignInsNow),
            value: formatCount(info.activeSignInsNow)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_ActiveReportsLoaded),
            value: formatCount(info.activeReportsLoaded)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_ActiveReportsRunningNow),
            value: formatCount(info.activeReportsRunningNow)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_LastScheduleRan),
            value: formatTimestamp(language, info.lastScheduleRan)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_TotalSchedulesRun),
            value: formatCount(info.totalSchedulesRun)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_SchedulesConfiguredTotal),
            value: formatCount(info.schedulesConfiguredTotal)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_SchedulesConfiguredActive),
            value: formatCount(info.schedulesConfiguredActive)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_InvalidPasswordAttemptsCurrent),
            value: formatCount(info.invalidPasswordAttemptsCurrent)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_InvalidPasswordAttemptsTotal),
            value: formatCount(info.invalidPasswordAttemptsTotal)
        },
        {
            label: language.get(UsersListLanguage.UsersList_MoreInfo_RestApiReportDataRequestsCount),
            value: formatCount(info.restApiReportDataRequestsCount)
        }
    ]
}

/**
 * Information icon for one users list row, showing that user's activity totals on hover.
 *
 * The totals are the same ones the classic user list puts in its Activity column tooltip. They are fetched the
 * first time the icon is hovered, so a list of many users costs nothing until a row is actually inspected, and
 * re-read once they go stale so the live sign-in and running-report counts stay honest.
 *
 * @param props - The user to describe and the language handler to label it with.
 * @returns The icon, wrapped in its tooltip.
 */
export const UsersListMoreInformation: FC<Props> = ({ username, language }: Props): ReactElement => {
    const [info, setInfo]: ReactState<Nullable<UserMoreInformation>> = useState<Nullable<UserMoreInformation>>(null)
    const [hasFailed, setHasFailed]: ReactState<boolean> = useState<boolean>(false)
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(false)
    const loadedAtRef: MutableRefObject<number> = useRef<number>(NOT_LOADED)
    // A row can be sorted or paged away while its request is in flight, and the reply must not then be applied.
    const isMountedRef: MutableRefObject<boolean> = useIsMountedRef()

    const loadInfo: () => void = useCallback((): void => {
        if (isLoading) return

        const isFresh: boolean =
            loadedAtRef.current !== NOT_LOADED && Date.now() - loadedAtRef.current < LIVE_TOTALS_TTL_MILLISECONDS

        if (isFresh) return

        setIsLoading(true)

        getUserMoreInformation(username)
            .then((response: JSONResponse<UserMoreInformation>): void => {
                if (!isMountedRef.current) return

                // Never present an empty payload as a full set of zeroes.
                if (!hasTotals(response)) {
                    setHasFailed(true)
                    return
                }

                loadedAtRef.current = Date.now()
                setInfo(response)
                setHasFailed(false)
            })
            .catch((): void => {
                if (isMountedRef.current) setHasFailed(true)
            })
            .finally((): void => {
                if (isMountedRef.current) setIsLoading(false)
            })
    }, [isLoading, isMountedRef, username])

    const buildTitle = (): ReactElement => {
        // A failed refresh of totals already on screen is reported rather than silently showing stale values.
        if (hasFailed) return <span>{language.get(UsersListLanguage.UsersList_MoreInfo_Unavailable)}</span>

        if (IsVarEmpty(info)) return <span>{language.get(UsersListLanguage.UsersList_MoreInfo_Loading)}</span>

        return (
            <span className='users-list-more-info-lines'>
                {buildLines(language, info).map(
                    (line: MoreInformationLine): ReactElement => (
                        <span key={line.label} className='users-list-more-info-line'>
                            {`${line.label}: ${line.value}`}
                        </span>
                    )
                )}
            </span>
        )
    }

    return (
        <Tooltip
            classes={{ tooltip: 'users-list-more-info-tooltip' }}
            title={buildTitle()}
            placement='left-start'
            enterDelay={300}
            interactive
            onOpen={loadInfo}
        >
            <span
                className='users-list-more-info-icon'
                aria-label={language.get(UsersListLanguage.UsersList_MoreInfo_Aria)}
                role='button'
                onFocus={loadInfo}
                tabIndex={0}
            >
                <InfoOutlinedIcon fontSize='small' />
            </span>
        </Tooltip>
    )
}
